import { useState } from "react";

// One "payment received now" line captured on a sale form. Shared by every sale
// modal (egg/bird/manure/bag) and the Daily Report sales step.
export interface SalePaymentLine {
  method: "cash" | "upi" | "bank_transfer" | "cheque";
  amount: number;
  reference?: string;
}

// Draft row while the operator types — amount is a string until submit.
export interface PaymentDraft {
  method: SalePaymentLine["method"];
  amount: string;
  reference: string;
}

export const paymentMethods: { value: SalePaymentLine["method"]; label: string }[] = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "cheque", label: "Cheque" },
];

// Cash never reaches the bank statement; every other method does, so it can be
// linked to a statement line later — a reference makes that match exact.
export const reconcilesViaBank = (m: SalePaymentLine["method"]) => m !== "cash";

export const PAYMENTS_EXCEED_MSG = "Payments exceed the sale total.";

export interface UseSalePayments {
  payments: PaymentDraft[];
  receivedSum: number;
  outstanding: number;
  exceedsTotal: boolean;
  reset: () => void;
  addPayment: () => void;
  removePayment: (i: number) => void;
  updatePayment: (i: number, patch: Partial<PaymentDraft>) => void;
  /** The valid (amount > 0) lines to send; cash lines drop their reference. */
  buildLines: () => SalePaymentLine[];
}

/**
 * Split-tender state for a sale form. `invoiceTotal` is the ROUNDED sale total
 * (the payload sends Math.round(total)), so the warning/disable, the submit
 * recheck, and the backend all agree on one basis (settledEpsilon = 0.005).
 * Only positive amounts count — the same predicate buildLines uses — so the
 * displayed Received can't diverge from what actually gets sent.
 */
export function useSalePayments(invoiceTotal: number): UseSalePayments {
  const [payments, setPayments] = useState<PaymentDraft[]>([]);
  return salePayments(payments, setPayments, invoiceTotal);
}

/**
 * The same split-tender model over payment lines held somewhere else — the
 * Daily Report keeps each sale row's lines in the row, so it has no hook per
 * row to call.
 */
export function salePayments(
  payments: PaymentDraft[],
  setPayments: (update: (ps: PaymentDraft[]) => PaymentDraft[]) => void,
  invoiceTotal: number,
): UseSalePayments {
  const receivedSum = payments.reduce((s, p) => s + Math.max(0, parseFloat(p.amount) || 0), 0);
  return {
    payments,
    receivedSum,
    outstanding: Math.max(0, invoiceTotal - receivedSum),
    exceedsTotal: receivedSum > invoiceTotal + 0.005,
    reset: () => setPayments(() => []),
    addPayment: () => setPayments((ps) => [...ps, { method: "cash", amount: "", reference: "" }]),
    removePayment: (i) => setPayments((ps) => ps.filter((_, idx) => idx !== i)),
    updatePayment: (i, patch) => setPayments((ps) => ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p))),
    buildLines: () =>
      payments
        .filter((p) => (parseFloat(p.amount) || 0) > 0)
        .map((p) => ({
          method: p.method,
          amount: parseFloat(p.amount),
          reference: reconcilesViaBank(p.method) && p.reference.trim() ? p.reference.trim() : undefined,
        })),
  };
}
